import { COMPANY_CONFIG } from './companyConfig';

export const POLICIES_DATA = {
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    lastUpdated: 'September 2026',
    summary: 'Global Bizness is committed to protecting your personal information and transaction confidentiality in accordance with applicable Indian laws.',
    sections: [
      {
        title: '1. Information We Collect',
        content: [
          'Personal Details: When you register on the Global Bizness platform, use our payment services, or sign up as a Retailer or Distributor, we may collect your name, official phone number, email address, postal address, store/business details, and government-issued identification (such as PAN or Aadhaar for KYC compliance where mandated by regulations).',
          'Transaction Data: We collect details of payments, utility bill collections, recharge history, consumer numbers, biller IDs, payment methods, transaction timestamps, and reference identifiers.',
          'Technical & Device Data: To protect against unauthorized access and maintain security, we collect IP addresses, browser specifications, operating system versions, and device identifiers.',
        ],
      },
      {
        title: '2. How We Use Your Information',
        content: [
          'To process and reconcile utility bill payments, mobile recharges, financial settlements, and travel bookings efficiently.',
          'To verify user and retailer identity, perform KYC verification, and prevent financial fraud or unauthorized transactions.',
          'To send instant transaction receipts, bill reminders, payment status alerts, and promotional announcements (which you may opt-out of at any time).',
          'To provide proactive customer service via our helpline (+91 7793965475) and email (Biznesspaysupport@gmail.com).',
          'To comply with statutory legal requirements and judicial orders under Indian law.',
        ],
      },
      {
        title: '3. Data Protection & Encryption',
        content: [
          'All communication between your device and Global Bizness servers is encrypted using industry-standard 256-bit SSL (Secure Sockets Layer) encryption.',
          'We enforce strict multi-tier access controls, firewalls, and continuous automated threat monitoring to safeguard your data against unauthorized interception or loss.',
          'Global Bizness does NOT sell, rent, or trade your personal data to any external marketing agencies.',
        ],
      },
      {
        title: '4. Sharing with Third Parties',
        content: [
          'We only share transaction-relevant information with authorized payment gateways, banking partners, Bharat Bill Payment System (BBPS) central units, and underlying utility service providers solely to complete your requested transactions.',
          'We may disclose information to law enforcement agencies if required by Indian judicial warrant or statutory mandates.',
        ],
      },
      {
        title: '5. Grievance Redressal & Contact',
        content: [
          `In case of any queries, privacy concerns, or data requests, you can contact our designated Grievance Officer:`,
          `Company: ${COMPANY_CONFIG.legalName}`,
          `Helpline / Phone: ${COMPANY_CONFIG.phone}`,
          `Official Support Email: ${COMPANY_CONFIG.email}`,
          `Registered Address: ${COMPANY_CONFIG.address.full}`,
        ],
      },
    ],
  },

  terms: {
    id: 'terms',
    title: 'Terms and Conditions',
    lastUpdated: 'September 2026',
    summary: 'These Terms of Service govern your access to and use of the Global Bizness website, partner portal, and financial transaction platform.',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: [
          `By accessing or using ${COMPANY_CONFIG.name}'s platform, website, or mobile application, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree with any portion of these terms, please discontinue using the platform immediately.`,
          'Global Bizness reserves the right to modify, amend, or update these terms at any time without prior individual notice. Continued use following any changes constitutes acceptance.',
        ],
      },
      {
        title: '2. User & Partner Eligibility',
        content: [
          'Users must be at least 18 years of age and legally competent to enter into binding contracts under the Indian Contract Act, 1872.',
          'Retailers and Distributors must provide true, accurate, and up-to-date business and personal KYC documents. Misrepresentation will result in immediate account termination and forfeiture of unsettled amounts.',
        ],
      },
      {
        title: '3. Utility Payments, Recharges & Booking Rules',
        content: [
          'Users and agents must verify consumer numbers, vehicle registration numbers, subscriber IDs, and bill amounts before authorizing any transaction.',
          'Global Bizness acts as a technology intermediary between customers, billers, and banking switches. While we ensure instantaneous transmission, the final reflection of bill credit depends upon the underlying utility board (e.g., TSSPDCL, BESCOM, etc.) as per their standard operating cycles.',
          'Global Bizness is not liable for delayed credits caused by upstream utility server outages, banking switch downtimes, or incorrect consumer numbers entered by the user.',
        ],
      },
      {
        title: '4. Code of Conduct & Prohibited Uses',
        content: [
          'Users and retail partners agree not to use the platform for any unlawful, fraudulent, or speculative activities, including unauthorized cash churning or money laundering.',
          'No user shall attempt to reverse-engineer, decompile, scrape, or exploit vulnerabilities in the Global Bizness software platform or POS terminals.',
        ],
      },
      {
        title: '5. Intellectual Property & Brand Rights',
        content: [
          'All trademarks, logos, visual graphics, system software, and domain content are the exclusive proprietary intellectual property of Global Bizness.',
          'Unauthorized reproduction or imitation of the Global Bizness logo, name, or service architecture is strictly prohibited under the Indian Copyright and Trademark Acts.',
        ],
      },
      {
        title: '6. Governing Law & Jurisdiction',
        content: [
          'These terms shall be governed by, construed, and enforced in accordance with the substantive laws of India.',
          'Any disputes, claims, or controversies arising out of or relating to these terms or services shall be subject to the exclusive jurisdiction of the competent courts in Hyderabad, Telangana, India.',
        ],
      },
    ],
  },

  refund: {
    id: 'refund',
    title: 'Refund, Cancellation & Returns Policy',
    lastUpdated: 'September 2026',
    summary: 'Clear, transparent rules regarding failed transaction reversals, biller disputes, and POS hardware return conditions.',
    sections: [
      {
        title: '1. Failed & Incomplete Transactions',
        content: [
          'If money is deducted from your bank account, credit card, debit card, or UPI wallet, but the transaction fails to complete or the utility bill is not generated, our automated reconciliation system will immediately flag the transaction.',
          'Automated Reversals: In most instances, the deducted amount will be automatically reversed back to the source bank account within 2 to 5 banking days without requiring manual intervention.',
          'If the reversal is not reflected after 5 banking days, please contact our support desk at +91 7793965475 or email Biznesspaysupport@gmail.com with your Transaction Reference ID.',
        ],
      },
      {
        title: '2. Disputed Utility & Recharge Payments',
        content: [
          'In cases where a recharge or bill payment is marked "Success" on the Global Bizness platform, but the operator or utility board has not reflected the balance, we initiate an inquiry with the biller.',
          'Investigation Turnaround: Inquiries are processed within 48 to 72 business hours.',
          'If the operator confirms non-receipt of funds, Global Bizness will initiate a full 100% refund to the original payment source within 7 working days.',
          'Please note: Once an electricity bill or mobile recharge has been successfully accepted and credited by the utility company or telecom provider, it cannot be canceled or recalled.',
        ],
      },
      {
        title: '3. Travel Bookings (Flights, Buses, Hotels)',
        content: [
          'Cancellation of flight tickets, bus bookings, and hotel stays are governed by the respective airline, bus operator, and hotel cancellation rules.',
          'Applicable airline or carrier cancellation penalties will be deducted, and the net refund amount will be credited back within 7 working days after receipt of funds from the carrier.',
          'Tatkal and non-refundable ticket quotas are non-refundable as per Indian Railways (IRCTC) guidelines.',
        ],
      },
      {
        title: '4. POS Hardware & Device Returns',
        content: [
          'POS Terminals, Micro-ATMs, and Soundboxes are covered by a 7-day hardware inspection window.',
          'If a device arrives in a damaged condition or possesses an inherent manufacturing defect, contact us within 7 days of delivery for a free immediate replacement or full device deposit return.',
          'Devices damaged due to physical abuse, liquid spillage, or unauthorized tampering are not eligible for free return.',
        ],
      },
      {
        title: '5. Refund Initiation Process & Timeline',
        content: [
          'Approved refunds are initiated within seven (7) working days of dispute resolution.',
          'Payment Gateway / Card refunds will be credited directly to the original card or bank account from which the payment originated.',
          'UPI payments will be credited directly to the original UPI Virtual Payment Address (VPA).',
        ],
      },
    ],
  },

  security: {
    id: 'security',
    title: 'Safety & Security Guidelines',
    lastUpdated: 'September 2026',
    summary: 'How Global Bizness protects your transactions, sensitive financial credentials, and digital safety.',
    sections: [
      {
        title: '1. Bank-Grade Encryption Architecture',
        content: [
          'All web and application endpoints are secured with high-grade 256-bit SSL encryption, ensuring complete data confidentiality between customer browsers and payment processing servers.',
          'No sensitive payment credentials like CVVs or ATM PINs are ever saved or stored on our servers.',
          'We deploy automated anomaly detection algorithms that scan for irregular transaction patterns, velocity spikes, and unauthorized login attempts in real time.',
        ],
      },
      {
        title: '2. Retailer & Distributor Security Best Practices',
        content: [
          'Never share your Global Bizness portal password, MPIN, or One-Time Passwords (OTPs) with anyone, including individuals claiming to be customer service staff.',
          'Global Bizness executives will NEVER call or message you asking for your password, PIN, or to download third-party screen-sharing apps (such as AnyDesk or TeamViewer).',
          'Always verify that the website URL in your browser begins with https:// and displays the authentic Global Bizness lock icon.',
        ],
      },
      {
        title: '3. Two-Factor Authentication (2FA)',
        content: [
          'All retailer and distributor logins are fortified with mandatory Two-Factor Authentication (OTP sent to registered mobile number).',
          'Biometric AePS cash withdrawals require thumbprint authentication verified live against the Aadhaar Central Identity Repository (CIDR).',
        ],
      },
      {
        title: '4. Immediate Incident Reporting',
        content: [
          'If you suspect unauthorized access to your account or notice an unfamiliar transaction, report it immediately to our 24/7 emergency response team:',
          `Hotline: ${COMPANY_CONFIG.phone}`,
          `Security Desk: ${COMPANY_CONFIG.email}`,
          'Your account can be instantly locked upon request to prevent unauthorized debits.',
        ],
      },
    ],
  },

  grievance: {
    id: 'grievance',
    title: 'Grievance Officer & Escalation Matrix',
    lastUpdated: 'September 2026',
    summary: 'Designated contact and escalation mechanism for dispute redressal under Information Technology Act and Consumer Protection rules.',
    sections: [
      {
        title: '1. Grievance Redressal Mechanism',
        content: [
          'In accordance with the Information Technology Act, 2000 and the Consumer Protection (E-Commerce) Rules, 2020, Global Bizness has designated a dedicated Grievance Officer to address user complaints and escalations promptly.',
          'All transaction disputes, delayed bill settlements, and refund inquiries will be acknowledged within 24 hours of receipt and aimed to be resolved within a maximum of 15 business days.',
        ],
      },
      {
        title: '2. Grievance Officer Contact Details',
        content: [
          `Company: ${COMPANY_CONFIG.legalName}`,
          `Designation: Principal Nodal Grievance Officer`,
          `Helpline Desk: ${COMPANY_CONFIG.phone}`,
          `Official Email: ${COMPANY_CONFIG.email}`,
          `Office Address: ${COMPANY_CONFIG.address.full}`,
          `Working Hours: Monday to Saturday, 9:30 AM to 6:30 PM IST (Excluding Public Holidays)`,
        ],
      },
      {
        title: '3. Escalation Procedure',
        content: [
          'Level 1 (Standard Support): Call our helpline at +91 7793965475 or email Biznesspaysupport@gmail.com with your transaction reference number.',
          'Level 2 (Grievance Desk): If your query remains unresolved after 48 business hours, submit an official escalation citing your Level 1 Ticket Number directly to the Grievance Officer at Biznesspaysupport@gmail.com.',
        ],
      },
    ],
  },

  regulatory: {
    id: 'regulatory',
    title: 'Regulatory & Compliance Information',
    lastUpdated: 'September 2026',
    summary: 'Statutory compliance details, corporate identity, and operating framework in India.',
    sections: [
      {
        title: '1. Corporate Identification',
        content: [
          `Business Name: ${COMPANY_CONFIG.legalName}`,
          'Registered Office: Fourth floor, unit number 405 to 4011, House number 1-98/3/5/23 to 27, Workflo, Hitex Bizness Square, Jubilee Enclave, Madhapur, Serilingampally, Hyderabad, Telangana - 500081, India.',
          'State of Jurisdiction: Telangana, India.',
        ],
      },
      {
        title: '2. Operating Model & Banking Partnerships',
        content: [
          'Global Bizness operates as a technology service provider facilitating digital utility bill payments, mobile recharges, POS retail tools, and travel booking services.',
          'Merchant onboarding, payment collections, and settlement of funds are facilitated through Reserve Bank of India (RBI) authorized banking partners, payment aggregators, and the Bharat Bill Payment System (BBPS) central framework in compliance with applicable statutory regulations.',
        ],
      },
      {
        title: '3. Security & Transaction Integrity',
        content: [
          'All customer and merchant sessions are protected with 256-Bit SSL bank-grade encryption.',
          'Transactions undergo real-time automated fraud and anomaly screening before processing across payment rails.',
        ],
      },
    ],
  },
};
